import React, { useEffect, useState } from "react";
import {
  Container,
  Table,
  Button,
  Form,
  Row,
  Col,
  Modal,
  Card,
  Badge
} from "react-bootstrap";
import axios from "axios";
import { pdf } from "@react-pdf/renderer";
import BalanceSheetPDF from "./BalanceSheetPDF";
import API_DOMAIN from "../../config/config";
import "./BalanceSheet.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaFilter, FaUndo, FaFilePdf, FaEye, FaEyeSlash, FaPlus, FaFileExcel } from "react-icons/fa";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const API_URL = `${API_DOMAIN}/balance.php`;

const BalanceSheet = () => {
  const [page, setPage] = useState(1);
  const rowsPerPage = 5;
  const [initialBalance, setInitialBalance] = useState(0);
  const [balance, setBalance] = useState(0);
  const [entries, setEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const today = new Date().toISOString().split("T")[0];

  const [totalPages, setTotalPages] = useState(1);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [expandedDate, setExpandedDate] = useState(null);
  const currentMonth = String(new Date().getMonth() + 1).padStart(2, "0");
  const currentYear = String(new Date().getFullYear());

  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [displayLimit, setDisplayLimit] = useState(50);
  const [loanLedger, setLoanLedger] = useState([]);


  const months = [
    { val: "01", label: "January" }, { val: "02", label: "February" },
    { val: "03", label: "March" }, { val: "04", label: "April" },
    { val: "05", label: "May" }, { val: "06", label: "June" },
    { val: "07", label: "July" }, { val: "08", label: "August" },
    { val: "09", label: "September" }, { val: "10", label: "October" },
    { val: "11", label: "November" }, { val: "12", label: "December" }
  ];

  const years = Array.from({ length: 5 }, (_, i) => String(new Date().getFullYear() - i));

  const fetchTransactions = async (date, offset = 0) => {
    setLoadingExpanded(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get_transactions_by_date",
        date: date,
        limit: 100,
        offset: offset
      });

      if (response.data.head.code === 200) {
        const newTrans = response.data.body.transactions || [];
        setExpandedTransactions(prev => offset === 0 ? newTrans : [...prev, ...newTrans]);
        setHasMoreExpanded(newTrans.length >= 100);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      toast.error("Failed to load details");
    } finally {
      setLoadingExpanded(false);
    }
  };

  const toggleDate = (date) => {
    if (expandedDate === date) {
      setExpandedDate(null);
      setExpandedTransactions([]);
    } else {
      setExpandedDate(date);
      setExpandedTransactions([]);
      fetchTransactions(date, 0);
    }
  };

  useEffect(() => {
    fetchBalance();
    fetchEntries();
  }, []);

  const fetchBalance = async () => {
    try {
      const response = await axios.post(API_URL, { action: "get_balance" });
      if (response.data.head.code === 200) {
        // Default balance to 1,000,000 if 0
        setBalance(response.data.body.balance || 1000000);
      }
    } catch (error) {
      console.error("Error fetching balance:", error);
    }
  };

  /* New State for Summary Data */
  const [summaryData, setSummaryData] = useState([]);
  const [expandedTransactions, setExpandedTransactions] = useState([]);
  const [expandedPage, setExpandedPage] = useState(1);
  const [loadingExpanded, setLoadingExpanded] = useState(false);
  const [hasMoreExpanded, setHasMoreExpanded] = useState(false); // To track if more transactions exist

  const fetchEntries = async (pageNumber = 1) => {
    try {
      const response = await axios.post(API_URL, {
        action: "list_summary_by_date",
        month: selectedMonth,
        year: selectedYear,
        page: pageNumber,
      });

      if (response.data.head.code === 200) {
        const fetchedSummary = response.data.body.summary || [];
        const openingBal = parseFloat(response.data.body.opening_balance || 0) + 1000000;

        // Process summary data to calculate opening/closing for each day
        let currentOpening = openingBal;
        const processedSummary = fetchedSummary.map(item => {
          const varavu = parseFloat(item.total_credit || 0);
          const patru = parseFloat(item.total_debit || 0);
          const closing = currentOpening + varavu - patru;

          const row = {
            date: item.d,
            opening: currentOpening,
            varavu: varavu,
            patru: patru,
            closing: closing
          };
          currentOpening = closing;
          return row;
        });

        setSummaryData(processedSummary);
        setInitialBalance(openingBal);
        setTotalPages(response.data.body.total_pages || 1);
        setPage(pageNumber);
      }
    } catch (error) {
      console.error("Error fetching summary:", error);
      toast.error("Failed to load summary");
    }
  };

  const addBalance = async () => {
    if (!description || !amount) {
      alert("Please enter description and amount");
      return;
    }

    try {
      const response = await axios.post(API_URL, {
        action: "add_transaction",
        description: description,
        amount: parseFloat(amount),
        type: "varavu",
      });

      if (response.data.head.code === 200) {
        toast.success("Transaction added successfully");
        setShowModal(false);
        setDescription("");
        setAmount("");
        fetchBalance();
        fetchEntries(page); // Refresh current page
      } else {
        alert("Failed to add transaction");
      }
    } catch (error) {
      console.error("Error adding transaction:", error);
    }
  };


  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const fetchAllTransactionsForExport = async () => {
    try {
      const response = await axios.post(API_URL, {
        action: "list_transactions",
        month: selectedMonth,
        year: selectedYear,
        page: 1,
        limit: 100000 // Ensure we get all records
      });
      if (response.data.head.code === 200) {
        return response.data.body.transactions || [];
      }
      return [];
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch export data");
      return [];
    }
  };

  const handleExcelExport = async () => {
    if (!summaryData || summaryData.length === 0) {
      toast.error("No data to export");
      return;
    }

    const loadToast = toast.loading("Preparing Excel export...");

    // Get transactions for the second sheet
    const allTransactions = await fetchAllTransactionsForExport();

    const excelData = summaryData.map((row) => {
      const profitLoss = row.varavu - row.patru;
      return {
        Date: new Date(row.date).toLocaleDateString("en-GB"),
        Opening: row.opening,
        "Credit (Varavu)": row.varavu,
        "Debit (Patru)": row.patru,
        "Profit/Loss": profitLoss,
        Closing: row.closing,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "BalanceSheet_Summary");

    // Add transactions sheet
    if (allTransactions && allTransactions.length > 0) {
      const transactionsData = allTransactions.map((entry) => ({
        Date: formatDate(entry.transaction_date),
        "Ref No": entry.receipt_no || "-",
        Description: entry.description,
        Type: entry.type === "patru" ? "Debit" : "Credit",
        Amount: parseFloat(entry.amount),
      }));
      const worksheetTrans = XLSX.utils.json_to_sheet(transactionsData);
      XLSX.utils.book_append_sheet(
        workbook,
        worksheetTrans,
        "All_Transactions"
      );
    }

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    saveAs(data, `BalanceSheet_${selectedMonth}_${selectedYear}.xlsx`);
    toast.dismiss(loadToast);
    toast.success("Excel exported successfully");
  };

  const handlePdfExport = async () => {
    if (!summaryData || summaryData.length === 0) {
      toast.error("No data to export");
      return;
    }

    const loadToast = toast.loading("Generating PDF...");
    const allTransactions = await fetchAllTransactionsForExport();

    if (!allTransactions || allTransactions.length === 0) {
      toast.update(loadToast, { render: "No transactions found for PDF", type: "warning", isLoading: false, autoClose: 3000 });
      return;
    }

    try {
      const blob = await pdf(
        <BalanceSheetPDF
          allEntries={allTransactions}
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          initialBalance={initialBalance}
          balance={balance}
        />
      ).toBlob();
      saveAs(blob, `BalanceSheet_${selectedMonth}_${selectedYear}.pdf`);
      toast.dismiss(loadToast);
      toast.success("PDF exported successfully");
    } catch (error) {
      console.error(error);
      toast.update(loadToast, { render: "Failed to generate PDF", type: "error", isLoading: false, autoClose: 3000 });
    }
  };

  const handleKurippuExcelExport = async () => {
    let dataToExport = loanLedger;

    if (!dataToExport || dataToExport.length === 0) {
      const toastId = toast.loading("Fetching Kurippu data...");
      try {
        const response = await axios.post(API_URL, {
          action: "list_transactions",
          month: selectedMonth,
          year: selectedYear,
          page: 1,
          limit: 10000 // Ensure we get enough records if the API simplifies pagination for this list
        });

        if (response.data.head.code === 200 && response.data.body.loan_ledger) {
          dataToExport = response.data.body.loan_ledger;
          setLoanLedger(dataToExport); // Cache it
          toast.dismiss(toastId);
        } else {
          toast.update(toastId, { render: "Failed to fetch data", type: "error", isLoading: false, autoClose: 3000 });
          return;
        }
      } catch (error) {
        toast.update(toastId, { render: "Network error", type: "error", isLoading: false, autoClose: 3000 });
        return;
      }
    }

    if (!dataToExport || dataToExport.length === 0) {
      toast.error("No loan ledger data found to export");
      return;
    }

    let currentBalance = initialBalance;
    let lastDate = null;

    const excelData = dataToExport.map((entry, index) => {
      const amount = entry.amount === "-" ? 0 : parseFloat(entry.amount);

      if (entry.type === "credit") currentBalance += amount;
      if (entry.type === "debit") currentBalance -= amount;

      const nextEntry = dataToExport[index + 1];
      const isLastEntryOfDate = !nextEntry || nextEntry.date !== entry.date;

      const row = {
        Date: entry.date !== lastDate
          ? new Date(entry.date).toLocaleDateString("en-GB")
          : "",
        Name: entry.name,
        'வரவு': entry.type === "credit" ? amount : "-",
        'பற்று': entry.type === "debit" ? amount : "-",
        Balance: isLastEntryOfDate ? currentBalance : ""
      };

      lastDate = entry.date;
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Kurippedu");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    saveAs(data, `Kurippu_${selectedMonth}_${selectedYear}.xlsx`);
  };





  return (
    <Container fluid className="p-4 bg-light min-vh-100">
      <style>
        {`
          .btn-custom-primary {
            background-color: #0757df !important;
            color: #ffffff !important;
            border: 1px solid #0757df !important;
          }
           .btn-custom-secondary {
            background-color: #f8f9fa !important;
            color: #212529 !important;
             border: 1px solid #ced4da !important;
          }
          .btn-custom-danger {
            background-color: #dc3545 !important;
             color: #ffffff !important;
             border: 1px solid #dc3545 !important;
          }
          .btn-custom-success {
             background-color: #198754 !important;
             color: #ffffff !important;
             border: 1px solid #198754 !important;
          }
          .btn-custom:disabled {
             opacity: 0.65;
          }
        `}
      </style>
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4 gap-3">
        <div>
          <h4 className="text-primary fw-bold mb-1">
            📘 Balance Sheet Report
          </h4>
          <p className="text-muted mb-0 small">
            Overview of your financial transactions
          </p>
        </div>
        <div className="bg-white px-4 py-3 rounded shadow-sm border d-flex align-items-center gap-3">
          <div className="p-2 bg-success bg-opacity-10 rounded-circle text-success">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="currentColor"
              className="bi bi-wallet2"
              viewBox="0 0 16 16"
            >
              <path d="M12.136.326A1.5 1.5 0 0 1 14 1.78V3h.5A1.5 1.5 0 0 1 16 4.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 0 13.5v-9a1.5 1.5 0 0 1 1.432-1.499L12.136.326zM5.562 3H13V1.78a.5.5 0 0 0-.621-.484L5.562 3zM1.5 4a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5h13a.5.5 0 0 0 .5-.5v-9a.5.5 0 0 0-.5-.5h-13z" />
            </svg>
          </div>
          <div>
            <span className="text-muted small d-block">Current Balance</span>
            <h4 className="mb-0 text-dark fw-bold">
              ₹{parseFloat(balance).toLocaleString()}
            </h4>
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Balance</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Amount</Form.Label>
              <Form.Control
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={addBalance}>
            Add Balance
          </Button>
        </Modal.Footer>
      </Modal>

      <Card className="shadow-sm border-0 mb-4">
        <Card.Header className="bg-white py-3 border-bottom-0">
          <h6 className="mb-0 fw-bold text-secondary d-flex align-items-center gap-2">
            <FaFilter /> Filter Options
          </h6>
        </Card.Header>
        <Card.Body className="pt-0">
          <Row className="g-3 align-items-end">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="text-muted fw-bold small">
                  Select Month
                </Form.Label>
                <Form.Select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="shadow-none"
                >
                  {months.map((m) => (
                    <option key={m.val} value={m.val}>
                      {m.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="text-muted fw-bold small">
                  Select Year
                </Form.Label>
                <Form.Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="shadow-none"
                >
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4} className="d-flex gap-2">
              <Button
                onClick={() => fetchEntries(1)}
                className="flex-grow-1 d-flex align-items-center justify-content-center gap-2 shadow-sm btn-custom btn-custom-primary"
              >
                <FaFilter /> Apply
              </Button>
              <Button
                className="border d-flex align-items-center justify-content-center gap-2 shadow-sm btn-custom btn-custom-secondary"
                onClick={() => {
                  setSelectedMonth(currentMonth);
                  setSelectedYear(currentYear);
                  fetchEntries(1);
                }}
              >
                <FaUndo /> Reset
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm border-0">
        <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center border-bottom-0">
          <h6 className="mb-0 fw-bold text-secondary">
            Summary & Transactions
          </h6>
          <div className="d-flex flex-column flex-sm-row gap-2">
            <Button
              size="sm"
              className="d-flex align-items-center gap-2 shadow-sm btn-custom btn-custom-success"
              onClick={handleKurippuExcelExport}


            >
              <FaFileExcel /> Kurippedu Export Excel
            </Button>
            <Button
              size="sm"
              className="d-flex align-items-center gap-2 shadow-sm btn-custom btn-custom-success"
              onClick={handleExcelExport}
            >
              <FaFileExcel /> Export Excel
            </Button>

            <Button
              size="sm"
              className="d-flex align-items-center gap-2 shadow-sm btn-custom btn-custom-danger"
              onClick={handlePdfExport}
            >
              <FaFilePdf /> Download PDF
            </Button>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover striped className="align-middle mb-0">
              <thead className="bg-light">
                <tr>
                  <th className="py-3 px-3 text-secondary small fw-bold text-uppercase">Date</th>
                  <th className="py-3 px-3 text-secondary small fw-bold text-uppercase text-end">Opening</th>
                  <th className="py-3 px-3 text-success small fw-bold text-uppercase text-end">Credit (Varavu)</th>
                  <th className="py-3 px-3 text-danger small fw-bold text-uppercase text-end">Debit (Patru)</th>
                  <th className="py-3 px-3 text-secondary small fw-bold text-uppercase text-center">Profit / Loss</th>
                  <th className="py-3 px-3 text-secondary small fw-bold text-uppercase text-end">Closing</th>
                  <th className="py-3 px-3 text-secondary small fw-bold text-uppercase text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(summaryData) && summaryData.length > 0 ? (
                  <>
                    {summaryData.map((row, index) => {
                      const profitLoss = row.varavu - row.patru;
                      const isProfit = profitLoss >= 0;
                      return (
                        <React.Fragment key={index}>
                          <tr>
                            <td className="px-3 fw-bold text-muted">{new Date(row.date).toLocaleDateString("en-GB")}</td>
                            <td className="px-3 text-end">₹{row.opening.toLocaleString()}</td>
                            <td className="px-3 text-end text-success fw-bold">₹{row.varavu.toLocaleString()}</td>
                            <td className="px-3 text-end text-danger fw-bold">₹{row.patru.toLocaleString()}</td>
                            <td className="px-3 text-center">
                              <Badge bg={isProfit ? "success" : "danger"} className="px-2 py-1">
                                {isProfit ? "+" : ""}{profitLoss.toLocaleString()}
                              </Badge>
                            </td>
                            <td className="px-3 text-end fw-bold">₹{row.closing.toLocaleString()}</td>
                            <td className="px-3 text-center">
                              <Button
                                size="sm"
                                variant={expandedDate === row.date ? "secondary" : "outline-primary"}
                                onClick={() => toggleDate(row.date)}
                                className="py-0 px-2"
                                style={{ fontSize: '0.8rem' }}
                                disabled={loadingExpanded && expandedDate === row.date}
                              >
                                {expandedDate === row.date ? (
                                  loadingExpanded ? (
                                    <><span className="text-black">Loading...</span></>
                                  ) : (
                                    <><FaEyeSlash className="me-1 text-black" /> <span className="text-black">Close</span></>
                                  )
                                ) : (
                                  <><FaEye className="me-1 " /> <span >View</span></>
                                )}
                              </Button>
                            </td>
                          </tr>

                          {expandedDate === row.date && (
                            <tr className="bg-light">
                              <td colSpan={7} className="p-4">
                                <div className="bg-white rounded border shadow-sm p-3">
                                  <h6 className="mb-3 border-bottom pb-2 fw-bold text-muted">Detailed Transactions for {new Date(row.date).toLocaleDateString("en-GB")}</h6>
                                  <Table size="sm" bordered hover className="mb-0">
                                    <thead className="bg-light">
                                      <tr>
                                        <th>Date</th>
                                        <th>Ref No</th>
                                        <th>Description</th>
                                        <th className="text-end">Credit</th>
                                        <th className="text-end">Debit</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {expandedTransactions.map((entry, idx) => (
                                        <tr key={idx}>
                                          <td>{formatDate(entry.transaction_date)}</td>
                                          <td>{entry.receipt_no || "-"}</td>
                                          <td>
                                            <span className={`badge bg-${entry.type === "patru" ? "danger" : "success"} me-2`}>
                                              {entry.type === "patru" ? "Dr" : "Cr"}
                                            </span>
                                            {entry.description}
                                          </td>
                                          <td className="text-end">{entry.type === "varavu" ? `₹${parseFloat(entry.amount || 0).toLocaleString()}` : "-"}</td>
                                          <td className="text-end">{entry.type === "patru" ? `₹${parseFloat(entry.amount || 0).toLocaleString()}` : "-"}</td>
                                        </tr>
                                      ))}
                                      {hasMoreExpanded && (
                                        <tr>
                                          <td colSpan={5} className="text-center p-2">
                                            <Button variant="link" size="sm" disabled={loadingExpanded} onClick={(e) => { e.stopPropagation(); fetchTransactions(row.date, expandedTransactions.length); }}>
                                              {loadingExpanded ? "Loading..." : "Load More..."}
                                            </Button>
                                          </td>
                                        </tr>
                                      )}
                                      <tr className="fw-bold bg-light border-top">
                                        <td colSpan={3} className="text-end">Daily Total</td>
                                        <td className="text-end text-success">
                                          ₹{expandedTransactions.filter(e => e.type === "varavu").reduce((sum, e) => sum + parseFloat(e.amount || 0), 0).toLocaleString()}
                                        </td>
                                        <td className="text-end text-danger">
                                          ₹{expandedTransactions.filter(e => e.type === "patru").reduce((sum, e) => sum + parseFloat(e.amount || 0), 0).toLocaleString()}
                                        </td>
                                      </tr>
                                    </tbody>
                                  </Table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}

                    {/* Grand Total Row */}
                    <tr className="bg-dark text-white fw-bold" style={{ borderTop: '2px solid black' }}>
                      <td colSpan={2} className="px-3 py-3 text-end fs-6 text-white">Grand Total Summary</td>
                      <td className="px-3 py-3 text-end text-success fs-6">
                        ₹{summaryData.reduce((acc, curr) => acc + curr.varavu, 0).toLocaleString()}
                      </td>
                      <td className="px-3 py-3 text-end text-danger fs-6">
                        ₹{summaryData.reduce((acc, curr) => acc + curr.patru, 0).toLocaleString()}
                      </td>
                      <td className="px-3 py-3 text-center fs-6">
                        <Badge bg={(summaryData.reduce((acc, curr) => acc + curr.varavu, 0) - summaryData.reduce((acc, curr) => acc + curr.patru, 0)) >= 0 ? 'success' : 'danger'}>
                          {(summaryData.reduce((acc, curr) => acc + curr.varavu, 0) - summaryData.reduce((acc, curr) => acc + curr.patru, 0)) >= 0 ? "+" : ""}
                          {(summaryData.reduce((acc, curr) => acc + curr.varavu, 0) - summaryData.reduce((acc, curr) => acc + curr.patru, 0)).toLocaleString()}
                        </Badge>
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                  </>
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center p-5 text-muted">
                      <div className="py-4">
                        <FaFilter size={32} className="mb-3 text-secondary opacity-50" />
                        <h5>No Records Found</h5>
                        <p className="mb-0">Try selecting a different month or year.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
          {/* Pagination */}
          <div className="d-flex justify-content-center align-items-center py-3 border-top bg-light">
            <Button variant="outline-secondary" size="sm" disabled={page === 1} onClick={() => fetchEntries(page - 1)}>
              Previous
            </Button>
            <span className="mx-3 small fw-bold text-muted">Page {page} of {totalPages}</span>
            <Button variant="outline-primary" size="sm" disabled={page >= totalPages} onClick={() => fetchEntries(page + 1)}>
              Next
            </Button>
          </div>
        </Card.Body>
      </Card>

      <ToastContainer />
    </Container>
  );
};

export default BalanceSheet;